import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  memo,
} from "react";
import MainLayout from "../sidebar/Sidebar.js";
import PhotoPlaceholder from "../components/PhotoPlaceholder.js";
import PhotoDetailModal from "../components/PhotoDetailModal.js";
import api from "../api/api.js";
import { Blurhash } from "react-blurhash";

// NEW imports for Supabase and auth
import { supabase } from "../supabase";
import { useAuth } from "../context/AuthContext.js";

export default function Gallery() {
  const scrollContainerRef = useRef(null);
  const sentinelRef = useRef(null);
  const [location, setLocation] = useState("Gallery");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [photos, setPhotos] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({
    current: 0,
    total: 0,
  });
  const [uploadError, setUploadError] = useState("");

  // Modal state
  const [selectedPhotoId, setSelectedPhotoId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // --- Prefetch tracking ---
  const prefetchRef = useRef({
    prefetchedCursors: new Set(), // Track which cursors we've already prefetched
    cache: new Map(), // Store prefetched pages by cursor
    currentPrefetch: null, // Track current prefetch promise to avoid duplicates
  });

  const { user } = useAuth();

  // Real-time subscription effect with secured Realtime auth
  useEffect(() => {
    if (!user) return;

    let active = true;
    let channel = null;

    (async () => {
      try {
        // Get a short-lived Supabase JWT from backend and set it on the Realtime client
        const resp = await api.get("/supabase/realtime-token");
        if (!active) return;
        const token = resp?.data?.token;
        if (!token) {
          console.warn("No Supabase Realtime token received");
          return;
        }
        supabase.realtime.setAuth(token);

        channel = supabase
          .channel("photo-updates")
          .on(
            "postgres_changes",
            {
              event: "UPDATE",
              schema: "public",
              table: "photo",
              filter: `user_id=eq.${user.uid}`,
            },
            async (payload) => {
              const updatedPhoto = payload.new;
              if (updatedPhoto.status === "processed") {
                try {
                  const response = await api.get(`/photos/${updatedPhoto.id}`);
                  const details = response.data;
                  setPhotos((prev) =>
                    prev.map((photo) =>
                      photo.id === updatedPhoto.id
                        ? {
                            ...photo,
                            thumbnail_url: details.thumbnail_url,
                            medium_url: details.medium_url,
                            original_url: details.original_url,
                            width: details.width || photo.width,
                            height: details.height || photo.height,
                            blur_hash: details.blur_hash || photo.blur_hash,
                            has_thumbnail: true,
                            status: "processed",
                          }
                        : photo
                    )
                  );
                  console.log(
                    `Updated photo ${updatedPhoto.id} from Supabase real-time`
                  );
                } catch (err) {
                  console.error(
                    `Failed to fetch details for photo ${updatedPhoto.id}:`,
                    err
                  );
                }
              }
            }
          )
          .subscribe();
      } catch (e) {
        console.error("Failed to initialize Supabase Realtime:", e);
      }
    })();

    return () => {
      active = false;
      if (channel) supabase.removeChannel(channel);
    };
  }, [user]);

  // Utility: find the actual scrolling container (handles MainLayout wrappers)
  const findScrollContainer = useCallback(() => {
    // If the explicit ref is scrollable, use it
    const prefer = scrollContainerRef.current;
    if (prefer) {
      const s = getComputedStyle(prefer);
      const scrollable = /(auto|scroll|overlay)/.test(
        `${s.overflowY} ${s.overflow}`
      );
      if (scrollable || prefer.scrollHeight > prefer.clientHeight)
        return prefer;
    }

    // Otherwise climb from the sentinel's ancestors
    let node = sentinelRef.current ? sentinelRef.current.parentElement : null;
    while (node && node !== document.body) {
      const style = getComputedStyle(node);
      const scrollable = /(auto|scroll|overlay)/.test(
        `${style.overflowY} ${style.overflow}`
      );
      if (scrollable && node.scrollHeight > node.clientHeight) return node;
      node = node.parentElement;
    }

    // Fallback to window
    return window;
  }, []);

  // Utility: compute scroll progress [0..1] for given container
  const getScrollProgress = useCallback((el) => {
    if (el === window) {
      const doc = document.documentElement;
      const body = document.body;
      const scrollTop = window.scrollY || doc.scrollTop || body.scrollTop || 0;
      const max =
        (doc.scrollHeight || body.scrollHeight) -
        (doc.clientHeight || window.innerHeight);
      if (max <= 0) return 1;
      return scrollTop / max;
    } else {
      const max = el.scrollHeight - el.clientHeight;
      if (max <= 0) return 1;
      return el.scrollTop / max;
    }
  }, []);

  // Load photos function
  const loadPhotos = useCallback(async () => {
    try {
      setLoading(true);
      setLoadingMore(false);
      setError("");

      // Reset prefetch state when reloading
      prefetchRef.current = {
        prefetchedCursors: new Set(),
        cache: new Map(),
        currentPrefetch: null,
      };

      const response = await api.get("/photos/", { params: { limit: 30 } });

      setPhotos(response.data.items || []);
      setNextCursor(response.data.next_cursor || null);
    } catch (err) {
      console.error("Error loading photos:", err);
      setError("Failed to load photos. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  // Prefetch next page (once per cursor) without updating UI yet
  const prefetchNextPage = useCallback(async () => {
    if (!nextCursor) return;

    const state = prefetchRef.current;

    // Skip if we've already prefetched this cursor or it's in progress
    if (state.prefetchedCursors.has(nextCursor)) {
      console.log("Prefetch skipped - already prefetched cursor:", nextCursor);
      return;
    }
    if (state.currentPrefetch) {
      console.log("Prefetch skipped - already in progress");
      return;
    }

    console.log("Starting prefetch for cursor:", nextCursor);

    // Mark this cursor as being prefetched
    state.prefetchedCursors.add(nextCursor);

    const prefetchPromise = api
      .get("/photos/", { params: { limit: 30, cursor: nextCursor } })
      .then((response) => {
        // Store the prefetched page in cache
        state.cache.set(nextCursor, {
          items: response.data.items || [],
          next_cursor: response.data.next_cursor || null,
        });
        console.log(
          "Prefetch successful - cached",
          response.data.items?.length || 0,
          "items"
        );
      })
      .catch((error) => {
        // On error, remove from prefetched set so we can try again
        state.prefetchedCursors.delete(nextCursor);
        console.warn("Prefetch failed:", error);
      })
      .finally(() => {
        state.currentPrefetch = null;
      });

    state.currentPrefetch = prefetchPromise;
    return prefetchPromise;
  }, [nextCursor]);

  // Load more photos (consumes prefetched page first if available)
  const loadMorePhotos = useCallback(async () => {
    if (loadingMore || !nextCursor) return;

    console.log("LoadMorePhotos called with cursor:", nextCursor);

    const state = prefetchRef.current;

    // Wait for any in-flight prefetch to complete
    if (state.currentPrefetch) {
      console.log("Waiting for in-flight prefetch to complete...");
      try {
        await state.currentPrefetch;
      } catch (error) {
        console.warn("Prefetch wait failed:", error);
      }
    }

    // Check if we have this page in cache
    if (state.cache.has(nextCursor)) {
      const cachedPage = state.cache.get(nextCursor);
      state.cache.delete(nextCursor); // Remove from cache after consuming

      console.log(
        "Using cached page - adding",
        cachedPage.items.length,
        "items"
      );
      setPhotos((prev) => [...prev, ...cachedPage.items]);
      setNextCursor(cachedPage.next_cursor);
      return;
    }

    // If not in cache, fetch normally
    console.log("No cache found - fetching normally");
    try {
      setLoadingMore(true);
      setError("");

      const response = await api.get("/photos/", {
        params: {
          limit: 30,
          cursor: nextCursor,
        },
      });

      // Mark this cursor as fetched to prevent duplicate prefetches
      state.prefetchedCursors.add(nextCursor);

      console.log("Fetched", response.data.items?.length || 0, "items");
      setPhotos((prev) => [...prev, ...(response.data.items || [])]);
      setNextCursor(response.data.next_cursor || null);
    } catch (err) {
      console.error("Error loading more photos:", err);
      setError("Failed to load more photos. Please try again.");
    } finally {
      setLoadingMore(false);
    }
  }, [nextCursor, loadingMore]);

  // Load photos on mount
  useEffect(() => {
    loadPhotos();
  }, [loadPhotos]);

  // Handle photo tile click - open modal
  const handlePhotoClick = useCallback((photoId) => {
    setSelectedPhotoId(photoId);
    setIsModalOpen(true);
  }, []);

  // Handle modal close
  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setSelectedPhotoId(null);
  }, []);

  const handleDelete = useCallback(
    async (photoId) => {
      if (!window.confirm("Are you sure you want to delete this photo?"))
        return;
      try {
        await api.delete(`/photos/${photoId}`);
        setPhotos((prev) => prev.filter((p) => p.id !== photoId));
        if (selectedPhotoId === photoId) {
          handleModalClose();
        }
      } catch (err) {
        console.error("Error deleting photo:", err);
        setError("Failed to delete photo. Please try again.");
      }
    },
    [selectedPhotoId, handleModalClose]
  );

  // Prefetch at 30% scroll of the correct container
  useEffect(() => {
    if (!nextCursor) return;

    const scrollEl = findScrollContainer();

    const onScroll = () => {
      const progress = getScrollProgress(scrollEl);
      if (progress >= 0.3) {
        // Prefetch will only happen once per cursor due to internal guards
        prefetchNextPage();
      }
    };

    // Check initial scroll position
    onScroll();

    if (scrollEl === window) {
      window.addEventListener("scroll", onScroll, { passive: true });
      window.addEventListener("resize", onScroll, { passive: true });
    } else {
      scrollEl.addEventListener("scroll", onScroll, { passive: true });
    }

    return () => {
      if (scrollEl === window) {
        window.removeEventListener("scroll", onScroll);
        window.removeEventListener("resize", onScroll);
      } else {
        scrollEl.removeEventListener("scroll", onScroll);
      }
    };
  }, [nextCursor, findScrollContainer, getScrollProgress, prefetchNextPage]);

  // When the sentinel nears the viewport, load more photos
  useEffect(() => {
    if (!nextCursor) return;

    const scrollEl = findScrollContainer();
    const rootForIO = scrollEl === window ? null : scrollEl;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Load more photos (will use cache if available)
            loadMorePhotos();
          }
        });
      },
      {
        root: rootForIO,
        rootMargin: "400px 0px", // trigger before reaching the end
        threshold: 0.0,
      }
    );

    const el = sentinelRef.current;
    if (el) observer.observe(el);

    return () => {
      if (el) observer.unobserve(el);
      observer.disconnect();
    };
  }, [nextCursor, loadMorePhotos, findScrollContainer]);

  // Memoized photo card for fewer re-renders
  const PhotoCard = useMemo(
    () =>
      memo(function PhotoCardInner({ photo, onClick, onDelete }) {
        const srcSet = useMemo(() => {
          const candidates = [];
          if (photo.thumbnail_url)
            candidates.push(`${photo.thumbnail_url} 256w`);
          if (photo.medium_url) candidates.push(`${photo.medium_url} 1024w`);
          if (photo.original_url && photo.width)
            candidates.push(`${photo.original_url} ${photo.width}w`);
          return candidates.join(", ");
        }, [photo]);

        const sizes = "(min-width: 768px) 25vw, (min-width: 640px) 33vw, 50vw";

        return (
          <div
            className="bg-white rounded-2xl shadow-md border border-gray-200 hover:border-green-500 hover:shadow-lg transition-all duration-300 overflow-hidden cursor-pointer"
            onClick={() => onClick(photo.id)}
          >
            <div className="bg-gray-100 relative overflow-hidden aspect-square">
              <div className="absolute inset-0">
                {photo.blur_hash ? (
                  <Blurhash
                    hash={photo.blur_hash}
                    width="100%"
                    height="100%"
                    resolutionX={32}
                    resolutionY={32}
                    punch={1}
                  />
                ) : (
                  <PhotoPlaceholder
                    width={photo.width}
                    height={photo.height}
                    className="w-full h-full flex items-center justify-center"
                    forceSquare={true}
                  />
                )}
              </div>
              {photo.has_thumbnail && photo.thumbnail_url && (
                <img
                  src={photo.thumbnail_url}
                  srcSet={srcSet || undefined}
                  sizes={srcSet ? sizes : undefined}
                  alt={photo.description || `Photo ID ${photo.id}`}
                  width={photo.width || undefined}
                  height={photo.height || undefined}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-cover opacity-0 transition-opacity duration-300"
                  onLoad={(e) => {
                    e.currentTarget.classList.remove("opacity-0");
                    e.currentTarget.classList.add("opacity-100");
                  }}
                />
              )}
            </div>
            <div className="p-3 sm:p-4 flex justify-between items-start">
              <div>
                <h3 className="font-medium text-gray-900 truncate text-sm sm:text-base">
                  {photo.width && photo.height
                    ? `${photo.width}×${photo.height}`
                    : "Unknown size"}
                </h3>
                <p className="text-xs sm:text-sm text-gray-500">
                  {new Date(photo.created_at).toLocaleDateString()}
                </p>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(photo.id);
                }}
                className="text-red-500 hover:text-red-700 transition-colors p-1"
                title="Delete photo"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </button>
            </div>
          </div>
        );
      }),
    []
  );

  // Helper function to get image dimensions
  const getImageDimensions = (file) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
      };
      img.onerror = () => {
        resolve({ width: null, height: null });
      };
      img.src = URL.createObjectURL(file);
    });
  };

  // Helper to compute blurhash from File (downsample for efficiency)
  const computeBlurHash = async (file) => {
    try {
      const { encode } = await import("blurhash"); // Dynamic import if needed for tree-shaking

      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          // Downsample to ~32px for fast encoding (blurhash doesn't need full res)
          const canvas = document.createElement("canvas");
          const maxDim = 32;
          const aspect = img.width / img.height;
          canvas.width = aspect >= 1 ? maxDim : Math.round(maxDim * aspect);
          canvas.height = aspect >= 1 ? Math.round(maxDim / aspect) : maxDim;

          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const bh = encode(
            imageData.data,
            imageData.width,
            imageData.height,
            4,
            3
          ); // 4x3 components for good quality
          URL.revokeObjectURL(img.src);
          resolve(bh);
        };
        img.onerror = (err) => {
          URL.revokeObjectURL(img.src);
          reject(err);
        };
        img.src = URL.createObjectURL(file);
      });
    } catch (error) {
      console.warn("Failed to compute blurhash:", error);
      return null; // Fallback: let backend handle it
    }
  };

  // Upload single photo using the same flow as camera-test-2
  const uploadSinglePhoto = async (file) => {
    try {
      const startResp = await api.post("/photos/start-upload");
      const { photo_id, post_url, post_fields } = startResp.data || {};

      if (!post_url || !post_fields || !photo_id) {
        throw new Error("Failed to start upload");
      }

      // Compute dimensions and blurhash in parallel
      const [dimensions, blurHash] = await Promise.all([
        getImageDimensions(file),
        computeBlurHash(file),
      ]);

      const s3Form = new FormData();
      Object.entries(post_fields).forEach(([k, v]) => s3Form.append(k, v));

      const contentType =
        file.type && file.type.startsWith("image/") ? file.type : "image/jpeg";
      s3Form.append("Content-Type", contentType);
      s3Form.append("file", file);

      // iOS Safari can throw TypeError: Load failed even when S3 accepted the upload (esp. with 204 responses)
      // We configure presigned POST for 201 responses, but still tolerate network errors by finalizing anyway.
      let s3NetworkError = false;
      try {
        const s3Resp = await fetch(post_url, { method: "POST", body: s3Form });
        if (!s3Resp.ok) {
          // Continue, backend finalize will verify object existence
          s3NetworkError = true;
        }
      } catch (e) {
        // Network error during S3 upload - continue to finalize as upload may have succeeded
        s3NetworkError = true;
        console.warn(`S3 network error for ${file.name}:`, e.message);
      }

      // Finalize with backend - this will verify the object exists in S3
      await api.post(`/photos/${photo_id}/complete`, {
        width: Number(dimensions.width) || undefined,
        height: Number(dimensions.height) || undefined,
        blur_hash: blurHash || undefined, // New: send client-computed blurhash
      });

      // Optimistically add photo
      setPhotos((prev) => [
        ...prev,
        {
          id: photo_id,
          created_at: new Date().toISOString(),
          width: Number(dimensions.width) || null,
          height: Number(dimensions.height) || null,
          blur_hash: blurHash || null,
          has_thumbnail: false,
          thumbnail_url: null,
          status: "processing",
        },
      ]);

      return {
        success: true,
        photo_id,
        filename: file.name,
        hadNetworkError: s3NetworkError,
      };
    } catch (error) {
      console.error(`Upload failed for ${file.name}:`, error);
      return { success: false, error: error.message, filename: file.name };
    }
  };

  // Handle multiple file uploads
  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    const imageFiles = files.filter((file) => file.type.startsWith("image/"));
    if (imageFiles.length === 0) {
      setUploadError("Please select image files only");
      return;
    }

    setUploading(true);
    setUploadError("");
    setUploadProgress({ current: 0, total: imageFiles.length });

    const results = [];

    for (let i = 0; i < imageFiles.length; i++) {
      const file = imageFiles[i];
      setUploadProgress({ current: i + 1, total: imageFiles.length });

      const result = await uploadSinglePhoto(file);
      results.push(result);

      if (i < imageFiles.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
    }

    const successful = results.filter((r) => r.success).length;
    const failed = results.filter((r) => !r.success);
    const networkErrors = results.filter(
      (r) => r.success && r.hadNetworkError
    ).length;

    if (failed.length > 0) {
      const failedNames = failed.map((f) => f.filename).join(", ");
      setUploadError(`${failed.length} uploads failed: ${failedNames}`);
    } else if (networkErrors > 0) {
      // Show a less alarming message for network errors that didn't affect the actual upload
      console.info(
        `${networkErrors} uploads had network errors but completed successfully`
      );
    }

    setUploading(false);
    setUploadProgress({ current: 0, total: 0 });

    event.target.value = "";

    if (successful > 0) {
      await loadPhotos();
    }
  };

  return (
    <MainLayout location={location} setLocation={setLocation}>
      <div ref={scrollContainerRef} className="bg-gray-50 min-h-full py-4 px-6">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Photo Gallery
            </h1>
            <p className="text-gray-600">View and manage your project photos</p>
          </div>

          {/* Search and Filter Bar */}
          <div className="flex flex-col sm:flex-row items-center justify-between mb-6 gap-4">
            <div className="flex gap-3">
              {["All", "Recent", "Projects", "Receipts"].map((filter) => (
                <button
                  key={filter}
                  className="px-4 py-2 rounded-full border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  {filter}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-3">
              <input
                type="text"
                placeholder="Search photos..."
                className="bg-gray-100 text-gray-900 placeholder-gray-400 rounded-md px-4 py-2 max-w-xs w-full focus:outline-none focus:ring-2 focus:ring-green-500"
              />
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileUpload}
                className="hidden"
                id="photo-upload-input"
                disabled={uploading}
              />
              <button
                onClick={() =>
                  document.getElementById("photo-upload-input").click()
                }
                disabled={uploading}
                className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-full w-10 h-10 flex items-center justify-center transition-colors duration-200 shadow-md hover:shadow-lg"
                title={uploading ? "Uploading..." : "Upload Photos"}
              >
                {uploading ? (
                  <svg
                    className="w-5 h-5 animate-spin"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                ) : (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Upload Progress */}
          {uploading && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-blue-800 font-medium">
                  Uploading Photos ({uploadProgress.current}/
                  {uploadProgress.total})
                </span>
                <span className="text-blue-600 text-sm">
                  {Math.round(
                    (uploadProgress.current / uploadProgress.total) * 100
                  )}
                  %
                </span>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{
                    width: `${
                      (uploadProgress.current / uploadProgress.total) * 100
                    }%`,
                  }}
                ></div>
              </div>
            </div>
          )}

          {/* Upload Error */}
          {uploadError && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <svg
                  className="w-5 h-5 text-yellow-600 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
                <span className="text-yellow-800">{uploadError}</span>
              </div>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mb-4"></div>
              <p className="text-gray-500">Loading photos...</p>
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
              <div className="text-red-600 mb-2">
                <svg
                  className="w-12 h-12 mx-auto mb-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-red-800 mb-1">
                Error Loading Photos
              </h3>
              <p className="text-red-600">{error}</p>
              <button
                onClick={loadPhotos}
                className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && photos.length === 0 && (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <svg
                  className="w-24 h-24 mx-auto"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">
                No Photos Yet
              </h3>
              <p className="text-gray-600 mb-6">
                Start by uploading some photos to your gallery
              </p>
              <button
                onClick={() =>
                  document.getElementById("photo-upload-input").click()
                }
                disabled={uploading}
                className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-6 py-3 rounded-lg transition-colors"
              >
                {uploading ? "Uploading..." : "Upload Your First Photos"}
              </button>
            </div>
          )}

          {/* Photo Grid */}
          {!error && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 sm:gap-6">
              {loading ? (
                [...Array(8)].map((_, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden"
                  >
                    <div className="aspect-square bg-gray-200 animate-pulse"></div>
                    <div className="p-3 sm:p-4">
                      <div className="h-4 bg-gray-200 rounded animate-pulse mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded animate-pulse w-2/3"></div>
                    </div>
                  </div>
                ))
              ) : (
                <>
                  {photos.map((photo) => (
                    <PhotoCard
                      key={photo.id}
                      photo={photo}
                      onClick={handlePhotoClick}
                      onDelete={handleDelete}
                    />
                  ))}
                  {loadingMore &&
                    [...Array(6)].map((_, index) => (
                      <div
                        key={`skeleton-${index}`}
                        className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden"
                      >
                        <div className="aspect-square bg-gray-200 animate-pulse"></div>
                        <div className="p-3 sm:p-4">
                          <div className="h-4 bg-gray-200 rounded animate-pulse mb-2"></div>
                          <div className="h-3 bg-gray-200 rounded animate-pulse w-2/3"></div>
                        </div>
                      </div>
                    ))}
                </>
              )}
            </div>
          )}

          {/* Sentinel for anticipatory loading */}
          <div ref={sentinelRef} style={{ height: 1 }} />

          {/* Load More Button */}
          {!loading && !error && photos.length > 0 && nextCursor && (
            <div className="flex justify-center mt-8">
              <button
                onClick={loadMorePhotos}
                disabled={loadingMore}
                className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg flex items-center gap-2"
              >
                {loadingMore ? (
                  <>
                    <svg
                      className="w-5 h-5 animate-spin"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Loading...
                  </>
                ) : (
                  "Load More"
                )}
              </button>
            </div>
          )}
      </div>

      {/* Photo Detail Modal */}
      <PhotoDetailModal
        photoId={selectedPhotoId}
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onDelete={handleDelete}
      />
    </MainLayout>
  );
}
